import { useMemo } from "react";
import { SectionCard } from "../../ui/section-card";
import { SectionForm } from "../../ui/section-form";
import { describeOutcome } from "../commands";
import { emptySubtypeValues, subtypeValuesFromRecord, subtypeValuesToInput } from "../form-mappers";
import { itemTypeLabels } from "../labels";
import { errorFieldsOf, type ItemSectionContext, useSectionState } from "./section-support";
import { subtypeFieldLabels, subtypeSections } from "./subtypes/registry";

const ID_PREFIX = "type-details";

/** The fields of the item's own type. The component for the type comes from the registry. */
export function SubtypeSection({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command } = ctx;
  const type = detail.item.itemType;
  const TypeFields = subtypeSections[type];
  const server = useMemo(
    () =>
      detail.subtype === null ? emptySubtypeValues(type) : subtypeValuesFromRecord(detail.subtype),
    [detail.subtype, type],
  );
  const section = useSectionState("type-details", server);

  async function submit() {
    const mapped = subtypeValuesToInput(type, section.values);
    if (!mapped.ok) {
      section.reject(mapped.errors);
      return;
    }
    section.clearErrors();
    const outcome = await command.run("updateSubtype", {
      expectedVersion: detail.item.version,
      subtype: mapped.value,
    });
    section.setMessage(describeOutcome(outcome, `${itemTypeLabels[type]} details saved.`));
    if (outcome.ok && outcome.detail.subtype !== null) {
      section.reset(subtypeValuesFromRecord(outcome.detail.subtype));
    }
  }

  const errorFields = errorFieldsOf(
    section.errors,
    Object.entries(subtypeFieldLabels[type]).map(([key, label]) => ({
      key,
      id: `${ID_PREFIX}-${key}`,
      label,
    })),
  );

  return (
    <SectionCard
      id="type-details"
      title={`${itemTypeLabels[type]} details`}
      description="The fields that belong to this type of item."
    >
      <SectionForm
        label={`${itemTypeLabels[type]} details`}
        dirty={section.dirty}
        pending={command.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={errorFields}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
        alwaysSubmittable={detail.subtype === null}
      >
        <TypeFields
          idPrefix={ID_PREFIX}
          values={section.values}
          errors={section.errors}
          disabled={readOnly || command.pending}
          onChange={(key, value) => section.setValues((current) => ({ ...current, [key]: value }))}
        />
      </SectionForm>
    </SectionCard>
  );
}
